import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useState } from "react";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { errorToast, handleError } from "../../../utils/common";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";
import { updateCreditDetailsSchema } from "../../../schemas/providers.schema";

const EditCreditDetailsModal = ({
  showEditCreditDetailsModal,
  setShowEditCreditDetailsModal,
  borrowerInfo,
  setBorrowerInfo,
}: any) => {
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();

  const handleSubmit = async (values: any, actions: any) => {
    if (
      values.disbursement_type === "self" &&
      !borrowerInfo?.bank_details?.length
    ) {
      errorToast("Please add bank details first");
      return;
    }

    const existingCreditDetails = borrowerInfo?.credit_details || {};

    const isFirstTime = existingCreditDetails?.used_limit == null;

    const payload = {
      borrower_id: borrowerInfo?.borrower_id,
      credit_details: {
        ...existingCreditDetails,
        nbfc_partner: values.nbfc_partner,
        emi_repayment_mode: values.emi_repayment_mode,
        credit_limit: values.credit_limit,
        interest_rate: values.interest_rate,
        processing_fee: values.processing_fee,
        late_fee: values.late_fee,
        disbursement_type: values.disbursement_type,
        ...(isFirstTime && {
          used_limit: "0",
        }),
      },
    };

    setLoading(true);

    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        setShowEditCreditDetailsModal(false);
        const resultResponse = result.data.response;
        setBorrowerInfo((prev: any) => ({
          ...prev,
          credit_details: {
            ...prev.credit_details,
            nbfc_partner: values.nbfc_partner,
            emi_repayment_mode: values.emi_repayment_mode,
            credit_limit: values.credit_limit,
            interest_rate: values.interest_rate,
            processing_fee: values.processing_fee,
            late_fee: values.late_fee,
            disbursement_type: values.disbursement_type,
            ...(isFirstTime && {
              used_limit: "0",
            }),
          },
        }));
      } else {
        errorToast(result.data.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={showEditCreditDetailsModal}
      onClose={() => setShowEditCreditDetailsModal(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <CustomDialogTitle
        id="customized-dialog-title"
        onClose={() => setShowEditCreditDetailsModal(false)}
      >
        Edit Credit Details
      </CustomDialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            nbfc_partner: borrowerInfo?.credit_details?.nbfc_partner || "",
            emi_repayment_mode:
              borrowerInfo?.credit_details?.emi_repayment_mode || "",
            credit_limit: borrowerInfo?.credit_details?.credit_limit || "",
            interest_rate: borrowerInfo?.credit_details?.interest_rate || "",
            processing_fee: borrowerInfo?.credit_details?.processing_fee || "",
            late_fee: borrowerInfo?.credit_details?.late_fee || "",
            disbursement_type:
              borrowerInfo?.credit_details?.disbursement_type || "self",
          }}
          validationSchema={updateCreditDetailsSchema}
          onSubmit={(values: any, actions: any) => {
            actions.setSubmitting(false);
            handleSubmit(values, actions);
          }}
        >
          {(props: any) => {
            const { values, touched, errors, handleBlur, setFieldValue } =
              props;
            return (
              <Form>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  mt={2}
                >
                  <TextField
                    select
                    fullWidth
                    label="NBFC Partner"
                    size="small"
                    value={values.nbfc_partner}
                    onChange={(e) =>
                      setFieldValue("nbfc_partner", e.target.value)
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={touched.nbfc_partner && Boolean(errors.nbfc_partner)}
                    helperText={touched.nbfc_partner && errors.nbfc_partner}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="getvantage">GetVantage</MenuItem>
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Repayment Tenure"
                    size="small"
                    value={values.emi_repayment_mode}
                    onChange={(e) =>
                      setFieldValue("emi_repayment_mode", e.target.value)
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={
                      touched.emi_repayment_mode &&
                      Boolean(errors.emi_repayment_mode)
                    }
                    helperText={
                      touched.emi_repayment_mode && errors.emi_repayment_mode
                    }
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="7">7</MenuItem>
                    <MenuItem value="15">15</MenuItem>
                    <MenuItem value="30">30</MenuItem>
                    <MenuItem value="60">60</MenuItem>
                    <MenuItem value="90">90</MenuItem>
                  </TextField>
                </Stack>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  mt={2}
                >
                  <TextField
                    fullWidth
                    label="Interest Rate (%)"
                    size="small"
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    value={values.interest_rate}
                    onChange={(e) =>
                      setFieldValue(
                        "interest_rate",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={
                      touched.interest_rate && Boolean(errors.interest_rate)
                    }
                    helperText={touched.interest_rate && errors.interest_rate}
                  />

                  <TextField
                    fullWidth
                    label="Processing Fees (%)"
                    size="small"
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    value={values.processing_fee}
                    onChange={(e) =>
                      setFieldValue(
                        "processing_fee",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={
                      touched.processing_fee && Boolean(errors.processing_fee)
                    }
                    helperText={touched.processing_fee && errors.processing_fee}
                  />
                </Stack>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  mt={2}
                >
                  <TextField
                    fullWidth
                    label="Late Fees (/day) %"
                    size="small"
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    value={values.late_fee}
                    onChange={(e) =>
                      setFieldValue(
                        "late_fee",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={touched.late_fee && Boolean(errors.late_fee)}
                    helperText={touched.late_fee && errors.late_fee}
                  />

                  <TextField
                    fullWidth
                    label="Credit Limit (₹)"
                    size="small"
                    type="number"
                    value={values.credit_limit}
                    onChange={(e) =>
                      setFieldValue(
                        "credit_limit",
                        e.target.value === "" ? "" : Number(e.target.value),
                      )
                    }
                    onBlur={handleBlur}
                    InputLabelProps={{ required: true }}
                    error={touched.credit_limit && Boolean(errors.credit_limit)}
                    helperText={touched.credit_limit && errors.credit_limit}
                  />
                </Stack>

                <FormControl
                  sx={{ mt: 1 }}
                  error={
                    touched.disbursement_type &&
                    Boolean(errors.disbursement_type)
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <FormLabel required>Disbursement Type</FormLabel>

                    <RadioGroup
                      row
                      value={values.disbursement_type}
                      defaultValue="self"
                      onChange={(e) =>
                        setFieldValue("disbursement_type", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="self"
                        control={<Radio />}
                        label="Self"
                      />
                      <FormControlLabel
                        value="third_party"
                        control={<Radio />}
                        label="Third Party"
                      />
                    </RadioGroup>
                  </Stack>

                  {touched.disbursement_type && errors.disbursement_type && (
                    <Box mt={0.5} sx={{ color: "#d32f2f", fontSize: 12 }}>
                      {errors.disbursement_type}
                    </Box>
                  )}
                </FormControl>

                <Box textAlign="center" mt={3}>
                  <LoadingButton
                    size="medium"
                    color="primary"
                    loading={loading}
                    loadingIndicator={
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    }
                    variant="contained"
                    type="submit"
                  >
                    Update
                  </LoadingButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditCreditDetailsModal;
